import { FSCollections } from '@src/constants/database';
import { ExecutionMapper } from '@src/models/mappers/execution.mapper';
import ExecutionRepository from '@src/repositories/ExecutionRepository';
import { sendErrorToSentry } from '@src/util/sentry/utils';

import ExecutionStateService from './ExecutionStateService';

class ExecutionService {
  constructor() {
    this.repository = new ExecutionRepository();
    this.executionStateService = new ExecutionStateService();
  }

  /**
   * Get execution with state and triggers
   * @param {string} organizationId - Organization ID
   * @param {string} divisionId - Division ID
   * @param {string} executionId - Execution ID
   * @returns {Promise<Object|null>} Execution data with state and triggers, or null if not found
   */
  async get(organizationId, divisionId, executionId) {
    try {
      const executionData = await this.repository.get(organizationId, divisionId, executionId);

      if (!executionData) {
        return null;
      }

      const errorContext = {
        functionName: 'ExecutionService.get',
        organizationId,
        divisionId,
        executionId,
      };

      // Get execution state
      try {
        const currentState = await this.executionStateService.getExecutionState(
          organizationId,
          divisionId,
          executionId,
        );

        if (currentState) {
          executionData.state = currentState;
        } else {
          const error = new Error('Execution state not found');
          sendErrorToSentry(error, errorContext);
        }
      } catch (stateError) {
        sendErrorToSentry(stateError, errorContext);
      }

      // Get execution triggers
      const triggers = await this.repository.getTriggers(organizationId, divisionId, executionId);

      if (triggers) {
        executionData.triggers = triggers;
      }

      return executionData;
    } catch (error) {
      sendErrorToSentry(error, {
        functionName: 'ExecutionService.get',
        organizationId,
        divisionId,
        executionId,
      });
      throw new Error(`Service error getting execution: ${error.message}`);
    }
  }

  async listElastic(params) {
    const filters = {};
    const sort = [{ field: 'createdAt._seconds', order: 'desc' }];
    const pagination = { from: params.from || 0, size: params.pageSize || 10 };
    if (params?.ids) {
      filters.terms = { id: params.ids };
    }
    const body = {
      entity: FSCollections.EXECUTIONS,
      organizationId: params.organizationId,
      filters,
      pagination,
      sort,
    };
    return await this.repository.listElastic(body);
  }


  /**
   * Latest executions of the given schedules (global sidebar search).
   * @param {import('@src/models/interfaces/execution.interface').ExecutionSearchSummariesParams} params
   * @returns {Promise<import('@src/models/interfaces/execution.interface').ExecutionSearchSummary[]>}
   */
  async searchSummaries(params) {
    if (!params?.orgId) throw new Error('Organization ID is required');

    const query = ExecutionMapper.toSearchSummariesQuery(params);
    if (!query) return [];

    const response = await this.repository.listElastic(query);
    return ExecutionMapper.fromElasticSearchSummariesResponse(response);
  }

  /**
   * Resolves execution document ids (and their division) matching the list filters.
   *
   * Id source for `useElasticFirestoreList`: Elastic `execution_records` decides
   * which task executions and in what order; Firestore materializes them.
   * @param {import('@src/models/interfaces/execution.interface').ExecutionSearchIdsParams} params
   * @returns {Promise<import('@src/models/interfaces/execution.interface').ExecutionSearchIdsResult>}
   */
  async searchIds(params) {
    if (!params?.orgId) throw new Error('Organization ID is required');

    const query = ExecutionMapper.toSearchIdsQuery(params);
    const response = await this.repository.listElastic(query);
    return ExecutionMapper.fromElasticSearchIdsResponse(response);
  }

  async getExecutionByIdElastic(organizationId, divisionId, executionId) {
    const pagination = { from: 0, size: 1 };

    const body = {
      entity: FSCollections.EXECUTIONS,
      organizationId,
      filters: {
        term: { id: executionId },
      },
      divisionIds: [divisionId],
      pagination,
    };
    const response = await this.repository.listElastic(body);
    return response?.hits?.hits?.[0]?._source || null;
  }
}

export default ExecutionService;
